import { useEffect, useMemo, useState } from 'react';
import { api, formatMoney, mediaUrl } from '@/lib/api';
import { Link } from 'react-router-dom';
import { RatingStars } from '@/components/marketplace/RatingStars';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Star, TrendingUp, Users, Package, ExternalLink, RefreshCcw, MessageCircle, ThumbsUp, Award } from 'lucide-react';

const StarBar = ({ label, count, total }) => {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
        <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-0.5 w-14 shrink-0">
                <span className="text-neutral-700">{label}</span>
                <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
            </div>
            <div className="flex-1 h-2 rounded-full bg-neutral-200 overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 text-right text-muted-foreground tabular-nums">{count}</span>
        </div>
    );
};

const RatingCard = ({ r, side }) => {
    const counter = side === 'received' ? r.rater : r.ratee;
    const roleLabel = side === 'received'
        ? (r.my_role === 'seller' ? 'Como vendedor' : r.my_role === 'buyer' ? 'Como comprador' : '')
        : (r.my_role === 'buyer' ? 'Yo como comprador' : r.my_role === 'seller' ? 'Yo como vendedor' : '');
    const roleColor = r.my_role === 'seller' ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : 'border-blue-500 text-blue-700 bg-blue-50';

    return (
        <div className="rounded-2xl border bg-card p-4 hover:shadow-md transition-shadow" data-testid={`rating-card-${r.id}`}>
            <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="h-11 w-11 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-semibold overflow-hidden shrink-0">
                    {counter?.avatar_url ? (
                        <img src={counter.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                        counter?.nickname?.[0]?.toUpperCase() || '?'
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to={counter?.nickname ? `/u/${counter.nickname}` : '#'} className="font-semibold text-sm hover:text-red-600 truncate">
                            @{counter?.nickname || 'usuario'}
                        </Link>
                        {roleLabel && <Badge variant="outline" className={`text-[10px] ${roleColor}`}>{roleLabel}</Badge>}
                        <span className="text-[11px] text-muted-foreground ml-auto">{new Date(r.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="mt-1"><RatingStars value={r.stars} size="md" /></div>
                    {r.comment && (
                        <p className="mt-2 text-sm text-foreground/85 whitespace-pre-line">{r.comment}</p>
                    )}
                    {r.ad && (
                        <Link to={`/ad/${r.ad.id}`} className="mt-3 flex items-center gap-3 rounded-lg border bg-secondary/40 p-2 hover:bg-secondary transition-colors">
                            <div className="h-10 w-10 rounded overflow-hidden bg-neutral-100 shrink-0">
                                {r.ad.media?.[0]?.url ? (
                                    <img src={mediaUrl(r.ad.media[0].url)} alt="" className="h-full w-full object-cover" />
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-neutral-400"><Package className="h-4 w-4" /></div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium truncate">{r.ad.title || 'Producto'}</div>
                                {r.ad.price !== undefined && <div className="text-[11px] text-muted-foreground">{formatMoney(r.ad.price, (r.ad.currency || 'mxn').toUpperCase())}</div>}
                            </div>
                            <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function RatingsPage() {
    const [data, setData] = useState({ received: [], given: [], summary: { total: 0, avg: 0, distribution: {}, as_seller: 0, as_buyer: 0 } });
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('received');
    const [search, setSearch] = useState('');
    const [starFilter, setStarFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/me/ratings');
            setData(data);
        } catch {} finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const summary = data.summary || {};
    const positive = useMemo(() => {
        const t = summary.total || 0;
        if (!t) return 0;
        const good = (summary.distribution?.[5] || 0) + (summary.distribution?.[4] || 0);
        return Math.round((good / t) * 100);
    }, [summary]);

    const items = data[tab] || [];
    const filtered = useMemo(() => {
        return items.filter(r => {
            if (starFilter !== 'all' && r.stars !== Number(starFilter)) return false;
            if (roleFilter !== 'all' && r.my_role !== roleFilter) return false;
            if (search) {
                const q = search.toLowerCase();
                const side = tab === 'received' ? r.rater : r.ratee;
                const inNick = (side?.nickname || '').toLowerCase().includes(q);
                const inComment = (r.comment || '').toLowerCase().includes(q);
                const inTitle = ((r.ad?.title) || '').toLowerCase().includes(q);
                if (!inNick && !inComment && !inTitle) return false;
            }
            return true;
        });
    }, [items, starFilter, roleFilter, search, tab]);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <img
                        src="/assets/graficos-odt-020226x-08.png"
                        alt="OK Califícalo"
                        className="h-10 sm:h-12 w-auto select-none"
                        data-testid="dashboard-okcalificalo-logo"
                        draggable={false}
                        onError={e => { e.target.src = '/assets/okcalificalo-logo.png'; }}
                    />
                    <h1 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <Award className="h-5 w-5 text-red-600" />Calificaciones
                    </h1>
                </div>
                <Button variant="outline" size="sm" onClick={load} data-testid="ratings-reload"><RefreshCcw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {/* Summary hero card */}
            <div className="rounded-2xl border bg-gradient-to-br from-red-50 via-white to-amber-50 p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Average */}
                    <div className="text-center md:text-left md:border-r md:pr-6">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Reputación general</div>
                        <div className="mt-2 flex items-end justify-center md:justify-start gap-2">
                            <div className="text-5xl sm:text-6xl font-bold text-red-600 tabular-nums" data-testid="ratings-avg">{summary.avg?.toFixed(1) || '0.0'}</div>
                            <div className="pb-2">
                                <RatingStars value={Math.round(summary.avg || 0)} size="md" />
                                <div className="text-[11px] text-muted-foreground mt-1">{summary.total || 0} calificaciones</div>
                            </div>
                        </div>
                        <div className="mt-3 flex flex-col md:flex-row gap-3 md:gap-4 items-center md:items-start justify-center md:justify-start text-xs">
                            <div className="flex items-center gap-1.5 text-emerald-700"><ThumbsUp className="h-3.5 w-3.5" /><span className="font-semibold">{positive}%</span> positivas</div>
                        </div>
                    </div>

                    {/* Distribution */}
                    <div className="space-y-1.5">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Distribución</div>
                        {[5, 4, 3, 2, 1].map(n => (
                            <StarBar key={n} label={n} count={summary.distribution?.[n] || 0} total={summary.total || 0} />
                        ))}
                    </div>

                    {/* Roles breakdown */}
                    <div className="space-y-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Recibidas por rol</div>
                        <div className="rounded-xl border bg-white p-3 flex items-center gap-3" data-testid="stat-as-seller">
                            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center"><Package className="h-5 w-5 text-emerald-700" /></div>
                            <div>
                                <div className="text-2xl font-bold tabular-nums">{summary.as_seller || 0}</div>
                                <div className="text-[11px] text-muted-foreground">Como vendedor</div>
                            </div>
                        </div>
                        <div className="rounded-xl border bg-white p-3 flex items-center gap-3" data-testid="stat-as-buyer">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center"><Users className="h-5 w-5 text-blue-700" /></div>
                            <div>
                                <div className="text-2xl font-bold tabular-nums">{summary.as_buyer || 0}</div>
                                <div className="text-[11px] text-muted-foreground">Como comprador</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs + filters */}
            <Tabs value={tab} onValueChange={setTab}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <TabsList>
                        <TabsTrigger value="received" data-testid="tab-received">Recibidas ({data.received.length})</TabsTrigger>
                        <TabsTrigger value="given" data-testid="tab-given">Dadas ({data.given.length})</TabsTrigger>
                    </TabsList>
                    <div className="flex flex-wrap gap-2 items-center">
                        <Input
                            placeholder="Buscar por usuario o producto..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-9 max-w-xs"
                            data-testid="ratings-search"
                        />
                        <Select value={starFilter} onValueChange={setStarFilter}>
                            <SelectTrigger className="h-9 w-32" data-testid="filter-stars"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas ★</SelectItem>
                                <SelectItem value="5">5 estrellas</SelectItem>
                                <SelectItem value="4">4 estrellas</SelectItem>
                                <SelectItem value="3">3 estrellas</SelectItem>
                                <SelectItem value="2">2 estrellas</SelectItem>
                                <SelectItem value="1">1 estrella</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="h-9 w-36" data-testid="filter-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los roles</SelectItem>
                                <SelectItem value="seller">{tab === 'received' ? 'Como vendedor' : 'Yo como vendedor'}</SelectItem>
                                <SelectItem value="buyer">{tab === 'received' ? 'Como comprador' : 'Yo como comprador'}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <TabsContent value="received" className="mt-4 space-y-3" data-testid="ratings-received">
                    {loading ? <div className="text-sm text-muted-foreground">Cargando…</div> :
                        filtered.length === 0 ? (
                            <div className="rounded-2xl border border-dashed p-10 text-center bg-card">
                                <Star className="h-10 w-10 mx-auto text-muted-foreground" />
                                <div className="mt-3 text-sm text-muted-foreground">
                                    {data.received.length === 0 ? 'Aún no tienes calificaciones recibidas.' : 'No hay resultados con los filtros aplicados.'}
                                </div>
                            </div>
                        ) : filtered.map(r => <RatingCard key={r.id} r={r} side="received" />)
                    }
                </TabsContent>

                <TabsContent value="given" className="mt-4 space-y-3" data-testid="ratings-given">
                    {loading ? <div className="text-sm text-muted-foreground">Cargando…</div> :
                        filtered.length === 0 ? (
                            <div className="rounded-2xl border border-dashed p-10 text-center bg-card">
                                <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground" />
                                <div className="mt-3 text-sm text-muted-foreground">
                                    {data.given.length === 0 ? 'Aún no has calificado a nadie.' : 'No hay resultados con los filtros aplicados.'}
                                </div>
                            </div>
                        ) : filtered.map(r => <RatingCard key={r.id} r={r} side="given" />)
                    }
                </TabsContent>
            </Tabs>
        </div>
    );
}
